import type { ReactNode } from 'react';
import { Box, Chip, Link, Typography } from '@mui/material';
import type { ExcelImportCategory } from '../../services/creditImportService';
import { formatHours, type ReviewCounts, type UnresolvedColumn } from '../../utils/creditImportValidation';

interface Props {
  counts: ReviewCounts;
  breakdown: { category: ExcelImportCategory; hours: number }[];
  unresolved: UnresolvedColumn[];
  onJumpToExceptions: () => void;
  onJumpToDuplicates: () => void;
}

/**
 * The reconciliation line: what will land, in hours, by field of study, and what still blocks it.
 * Zero segments are omitted. Exception and duplicate counts are links to their rows.
 */
export default function ImportSummaryBand({ counts, breakdown, unresolved, onJumpToExceptions, onJumpToDuplicates }: Props) {
  const segments: ReactNode[] = [];
  segments.push(
    <span key="ready">
      <strong>{counts.ready}</strong> ready · <strong>{formatHours(counts.readyHours)}</strong> hours
    </span>,
  );
  if (counts.skipped > 0) {
    const label = counts.skippedDuplicates === counts.skipped
      ? `skipped as ${counts.skipped === 1 ? 'a duplicate' : 'duplicates'}`
      : 'skipped';
    segments.push(
      <span key="skipped">
        <Link component="button" type="button" underline="hover" onClick={onJumpToDuplicates} sx={{ font: 'inherit', verticalAlign: 'baseline' }}>
          <strong>{counts.skipped}</strong> {label}
        </Link>
      </span>,
    );
  }
  if (counts.errors > 0) {
    segments.push(
      <span key="errors">
        <Link component="button" type="button" underline="hover" onClick={onJumpToExceptions} sx={{ font: 'inherit', verticalAlign: 'baseline', color: 'status.attention.fg' }}>
          <strong>{counts.errors}</strong> need{counts.errors === 1 ? 's' : ''} attention
        </Link>
      </span>,
    );
  }
  if (counts.removed > 0) {
    segments.push(
      <span key="removed">
        <strong>{counts.removed}</strong> removed
      </span>,
    );
  }

  const unresolvedHours = unresolved.reduce((s, c) => s + c.hours, 0);

  return (
    <Box component="section" aria-label="Import summary" sx={{ mb: 2 }}>
      <Typography variant="h6" component="p" sx={{ fontWeight: 600 }} aria-live="polite" data-testid="credit-import-counts">
        {segments.map((s, i) => (
          <span key={i}>
            {i > 0 && <span aria-hidden="true"> · </span>}
            {s}
          </span>
        ))}
      </Typography>

      {breakdown.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }} aria-label="Hours by field of study">
          {breakdown.map(({ category, hours }) => (
            <Chip
              key={category.id}
              size="small"
              label={`${category.name} ${formatHours(hours)}`}
              sx={{ bgcolor: 'tint.primarySoft', color: 'primary.dark', fontWeight: 600 }}
            />
          ))}
        </Box>
      )}

      {unresolved.length > 0 && (
        <Typography variant="body2" sx={{ mt: 1, color: 'status.attention.fg', fontWeight: 600 }}>
          {unresolved.length === 1
            ? `1 column with ${formatHours(unresolvedHours)} hours needs a decision below.`
            : `${unresolved.length} columns with ${formatHours(unresolvedHours)} hours need a decision below.`}
        </Typography>
      )}
    </Box>
  );
}
